// Speicherungen auf dem Gerät nur mit Einwilligung (Datenstrategie D0, 27.10.2026, § 25 TDDDG).
//
// § 25 Abs. 1 TDDDG erfasst jedes Speichern und Auslesen auf dem Endgerät (Cookie, localStorage,
// sessionStorage), egal ob personenbezogen. Ausgenommen ist nur, was für den ausdrücklich
// gewünschten Dienst unbedingt erforderlich ist (Abs. 2 Nr. 2): Login, die Banner-Auswahl selbst,
// Entwürfe, angefangene Buchung, App-Merker. Alles, was nur der Messung oder Werbung dient, läuft
// über die Regeln hier:
//
//   Statistik (Banner „Statistik / Analyse“): Besucherkennung cc_vid, A/B-Merker, Sitzungskennung
//     der Website-Messung (cc_sid), Kampagnen-Herkunft (utm_params, first_touch), Quiz-Ergebnis.
//   Marketing (Banner „Marketing“): Klick-IDs der Werbeplattformen (click_ids, last_paid_click,
//     _fbc, cc_gclid …), Kontaktdaten für die Conversion-Meldung (schnuppertag_lead).
//
// Ohne Einwilligung bleibt alles im Arbeitsspeicher der Seite (gleiches Muster wie die
// Nutzungsmessung im Kundenbereich seit 08.10.2026): Die Messung läuft weiter, nur ohne Kennung auf
// dem Gerät. In der App wird für die Website-Messung nie etwas gespeichert (Zusage im
// Datenschutztext „Nutzungsmessung in App und Kundenbereich“), auch nicht mit Statistik-Einwilligung.
//
// Bewusst ohne weitere Imports außer consentSpeicher/platform: das Modul liegt im Einstieg.

import { gespeicherteEinwilligung, type GespeicherteEinwilligung } from "@/lib/consentSpeicher";
import { isNative } from "@/lib/platform";

type Einwilligung = GespeicherteEinwilligung | null;

/** Darf die eigene Website-Messung (A/B, Sitzung, Kampagnen-Herkunft) etwas auf dem Gerät ablegen? */
export function darfMessungSpeichern(e: Einwilligung = gespeicherteEinwilligung()): boolean {
  return !isNative() && e?.analytics === true;
}

/** Dürfen Klick-IDs der Werbeplattformen und Daten für Conversion-Meldungen auf das Gerät? */
export function darfMarketingSpeichern(e: Einwilligung = gespeicherteEinwilligung()): boolean {
  return !isNative() && e?.marketing === true;
}

// ── Aufräumen ────────────────────────────────────────────────────────────────
// Bis 27.10.2026 wurden die Statistik-Speicherungen ohne Einwilligung angelegt. Was nicht (mehr)
// erlaubt ist, wird entfernt: beim Start (Altbestand) und bei jeder Banner-Entscheidung (Widerruf).

const STATISTIK = {
  cookies: ["cc_vid", "utm_params", "first_touch", "cc_quiz"],
  cookiePraefixe: ["_ga", "_clck", "_clsk", "_cs_"],
  local: ["cc_vid", "ab_session_id", "ab_variant"],
  localPraefixe: ["ab_exposed_"],
  session: ["cc_sid", "cc_sid_attr", "utm_params"],
};

const MARKETING = {
  cookies: ["click_ids", "last_paid_click", "_fbc", "_fbp", "cc_gclid", "cc_gbraid", "cc_wbraid"],
  cookiePraefixe: ["_gcl_"],
  local: [] as string[],
  localPraefixe: [] as string[],
  session: ["click_ids", "schnuppertag_lead"],
};

function cookieNamen(): string[] {
  try {
    return document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function cookieLoeschen(name: string): void {
  try {
    const ablauf = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    document.cookie = `${name}${ablauf}`;
    const host = location.hostname;
    document.cookie = `${name}${ablauf}; domain=${host}`;
    // Google/Meta setzen ihre Cookies auf die Hauptdomain (.charliescrew.com).
    const basis = host.replace(/^www\./, "");
    if (basis !== host || basis.includes(".")) document.cookie = `${name}${ablauf}; domain=.${basis}`;
  } catch {
    /* Aufräumen darf nie die Seite stören */
  }
}

function speicherLeeren(speicher: Storage, namen: string[], praefixe: string[]): void {
  try {
    for (const n of namen) speicher.removeItem(n);
    if (!praefixe.length) return;
    const weg: string[] = [];
    for (let i = 0; i < speicher.length; i++) {
      const k = speicher.key(i);
      if (k && praefixe.some((p) => k.startsWith(p))) weg.push(k);
    }
    weg.forEach((k) => speicher.removeItem(k));
  } catch {
    /* Storage gesperrt: dann liegt dort auch nichts */
  }
}

function gruppeEntfernen(g: typeof STATISTIK, vorhandeneCookies: string[]): void {
  for (const name of vorhandeneCookies) {
    if (g.cookies.includes(name) || g.cookiePraefixe.some((p) => name.startsWith(p))) cookieLoeschen(name);
  }
  try {
    speicherLeeren(localStorage, g.local, g.localPraefixe);
  } catch {
    /* ignore */
  }
  try {
    speicherLeeren(sessionStorage, g.session, []);
  } catch {
    /* ignore */
  }
}

/**
 * Entfernt Mess- und Werbe-Speicherungen, für die keine Einwilligung (mehr) vorliegt.
 * Ohne Entscheidung im Banner gilt: keine Einwilligung. Wirft nie, schreibt nur, wenn etwas da ist.
 */
export function nichtErlaubteSpeicherungenEntfernen(e: Einwilligung = gespeicherteEinwilligung()): void {
  if (typeof window === "undefined") return;
  const cookies = cookieNamen();
  if (!darfMessungSpeichern(e)) gruppeEntfernen(STATISTIK, cookies);
  if (!darfMarketingSpeichern(e)) gruppeEntfernen(MARKETING, cookies);
}
